import React, { useEffect, useState } from 'react';
import { Modal, Table, Tabs, Tag, Typography, Descriptions, Spin, Alert, Button, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { inventoryService, InventoryLot, Medicine, InventoryHistory } from '../../services/inventoryService';
import { formatDate, formatCurrency } from '../../utils/formatters';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface InventoryDetailModalProps {
  visible: boolean;
  medicineId: number | null;
  onClose: () => void;
}

const InventoryDetailModal: React.FC<InventoryDetailModalProps> = ({
  visible,
  medicineId,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [medicine, setMedicine] = useState<Medicine | null>(null);
  const [lots, setLots] = useState<InventoryLot[]>([]);
  const [history, setHistory] = useState<InventoryHistory[]>([]);
  const [activeTab, setActiveTab] = useState('lots');

  useEffect(() => {
    if (visible && medicineId) {
      loadInventoryDetails();
    } else if (!visible) {
      // Reset states when modal is closed
      setMedicine(null);
      setLots([]);
      setHistory([]);
      setActiveTab('lots');
    }
  }, [visible, medicineId]);

  const loadInventoryDetails = async () => {
    if (!medicineId) return;
    
    setLoading(true);
    try {
      const details = await inventoryService.getInventoryDetails(medicineId);
      setMedicine(details.medicine);
      setLots(details.lots);
      
      // Load history separately to avoid blocking main data
      loadInventoryHistory();
    } catch (error) {
      console.error('Error loading inventory details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInventoryHistory = async () => {
    if (!medicineId) return;
    
    setHistoryLoading(true);
    try {
      const historyData = await inventoryService.getInventoryHistory(medicineId);
      setHistory(historyData || []);
    } catch (error) {
      console.error('Error loading inventory history:', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const lotsColumns = [
    {
      title: 'Số lô',
      dataIndex: 'lot_number',
      key: 'lot_number',
      width: 120,
    },
    {
      title: 'Hạn sử dụng',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      width: 120,
      render: (date: string) => {
        const expiryDate = new Date(date);
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        let color = 'green';
        if (daysUntilExpiry <= 30) color = 'red';
        else if (daysUntilExpiry <= 90) color = 'orange';
        
        return (
          <Tag color={color}>
            {formatDate(date)}
          </Tag>
        );
      },
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right' as const,
    },
    {
      title: 'Giá nhập',
      dataIndex: 'cost_price',
      key: 'cost_price',
      width: 120,
      align: 'right' as const,
      render: (price: number) => formatCurrency(price),
    },
    {
      title: 'Nhà cung cấp',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 150,
    },
    {
      title: 'Ngày nhập',
      dataIndex: 'import_date',
      key: 'import_date',
      width: 120,
      render: (date: string) => formatDate(date),
    },
  ];

  const historyColumns = [
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'IN' ? 'green' : 'red'}>
          {type === 'IN' ? 'Nhập' : 'Xuất'}
        </Tag>
      ),
    },
    {
      title: 'Số phiếu',
      dataIndex: 'voucher_number',
      key: 'voucher_number',
      width: 120,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const getStatusConfig = () => {
          switch (status) {
            case 'completed':
              return { color: 'green', text: 'Hoàn thành' };
            case 'pending':
              return { color: 'orange', text: 'Chờ xử lý' };
            case 'cancelled':
              return { color: 'red', text: 'Đã hủy' };
            default:
              return { color: 'gray', text: 'Không xác định' };
          }
        };
        const config = getStatusConfig();
        return (
          <Tag color={config.color}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right' as const,
      render: (quantity: number) => (
        <Text type={quantity > 0 ? 'success' : 'danger'}>
          {quantity > 0 ? '+' : ''}{quantity}
        </Text>
      ),
    },
    {
      title: 'Số lô',
      dataIndex: 'lot_number',
      key: 'lot_number',
      width: 120,
    },
    {
      title: 'Hạn sử dụng',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      width: 120,
      render: (date: string) => date ? formatDate(date) : 'N/A',
    },
    {
      title: 'Khách hàng/NCC',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 150,
    },
    {
      title: 'Người tạo',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 120,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => formatDate(date),
    },
  ];

  const getStockStatus = () => {
    if (!medicine) return null;
    
    const totalStock = lots.reduce((sum, lot) => sum + lot.quantity, 0);
    const isLowStock = totalStock <= medicine.min_stock_level;
    
    return (
      <Alert
        message={isLowStock ? 'Tồn kho thấp' : 'Tồn kho ổn định'}
        description={`Tổng tồn kho: ${totalStock} ${medicine.unit} (Mức tối thiểu: ${medicine.min_stock_level} ${medicine.unit})`}
        type={isLowStock ? 'warning' : 'success'}
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  };

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Chi tiết tồn kho
          </Title>
          {medicine && (
            <Text type="secondary">
              {medicine.name} ({medicine.code})
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {medicine && (
          <>
            <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Tên thuốc" span={2}>
                {medicine.name}
              </Descriptions.Item>
              <Descriptions.Item label="Mã thuốc">
                {medicine.code}
              </Descriptions.Item>
              <Descriptions.Item label="Hoạt chất">
                {medicine.active_ingredient || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Dạng bào chế">
                {medicine.dosage_form}
              </Descriptions.Item>
              <Descriptions.Item label="Hàm lượng">
                {medicine.strength || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Đơn vị">
                {medicine.unit}
              </Descriptions.Item>
              <Descriptions.Item label="Nhà sản xuất">
                {medicine.manufacturer || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Giá bán">
                {formatCurrency(medicine.price)}
              </Descriptions.Item>
            </Descriptions>

            {getStockStatus()}

            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane tab={`Lô hàng (${lots.length})`} key="lots">
                <Table
                  dataSource={lots}
                  columns={lotsColumns}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 800 }}
                />
              </TabPane>
              <TabPane tab={`Lịch sử (${history.length})`} key="history">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Text type="secondary">Lịch sử nhập xuất kho của thuốc</Text>
                    <Button 
                      size="small"
                      icon={<ReloadOutlined />} 
                      onClick={loadInventoryHistory}
                      loading={historyLoading}
                    >
                      Làm mới
                    </Button>
                  </div>
                  <Table
                    dataSource={history}
                    columns={historyColumns}
                    rowKey={(record, index) => `${record.voucher_number}-${record.type}-${index}`}
                    pagination={{ 
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} giao dịch`
                    }}
                    size="small"
                    scroll={{ x: 1100 }}
                    loading={historyLoading}
                    locale={{
                      emptyText: history.length === 0 && !historyLoading ? 'Chưa có lịch sử giao dịch nào' : undefined
                    }}
                  />
                </Space>
              </TabPane>
            </Tabs>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default InventoryDetailModal; 